import { useState, useEffect } from "react";
import AntDTable from "@/common/components/ui/antd-table";
import ScreenerService from "@/services/Screener/APIs/ScreenerService";
import {
  ShareholdingMoreThan1PercentRow,
  ShareholdingPatternRow,
  CompanyBoardOfDirectorsItem,
  SubsidiaryJVCollaborationRow,
  BankersRow,
  Company,
} from "@/utils/types/Screener/Screener";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/common/components/ui/select";

interface OwnershipTabProps {
  company: Company;
}

const OwnershipTab = ({ company }: OwnershipTabProps) => {
  const [selectedDirectorYear, setSelectedDirectorYear] = useState(2025);
  const [fromYear, setFromYear] = useState("2016");
  const [toYear, setToYear] = useState("2026");
  const [shareholdingData, setShareholdingData] = useState<
    ShareholdingMoreThan1PercentRow[]
  >([]);
  const [isShareholdingLoading, setIsShareholdingLoading] = useState(false);
  const [boardData, setBoardData] = useState<CompanyBoardOfDirectorsItem[]>([]);
  const [isBoardLoading, setIsBoardLoading] = useState(false);
  const [subsidiaryJVData, setSubsidiaryJVData] = useState<
    SubsidiaryJVCollaborationRow[]
  >([]);
  const [availableYears, setAvailableYears] = useState<number[]>([2025]);
  const [bankersData, setBankersData] = useState<BankersRow[]>([]);
  const [showAllBankers, setShowAllBankers] = useState(false);
  const [shareholdingPattern, setShareholdingPattern] = useState<
    ShareholdingPatternRow[]
  >([]);
  const [selectedYRC, setSelectedYRC] = useState<number | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!company?.id && !company?.co_code) {
        return;
      }
      const id = company.co_code?.toString() ?? company.id?.toString();

      setIsShareholdingLoading(true);
      setIsBoardLoading(true);

      try {
        const [shareholdingRes, boardRes, subJVRes, bankersRes] =
          await Promise.all([
            ScreenerService.getShareholdingMoreThan1PercentData(id),
            ScreenerService.getCompanyBoardOfDirectors(id),
            ScreenerService.getSubsidiaryJVCollaborationData(id),
            ScreenerService.getBankersData(id),
          ]);

        if (shareholdingRes?.success && shareholdingRes?.data) {
          setShareholdingData(shareholdingRes.data);
        }

        if (boardRes?.success && boardRes?.data) {
          setBoardData(boardRes.data);
          const years = Array.from(
            new Set(boardRes.data.map(item => item.year))
          ).sort((a, b) => b - a);
          if (years.length > 0) {
            setAvailableYears(years);
            setSelectedDirectorYear(years[0]);
          }
        }

        if (subJVRes?.success && subJVRes?.data) {
          setSubsidiaryJVData(subJVRes.data);
        }

        if (bankersRes?.success && bankersRes?.data) {
          setBankersData(bankersRes.data);
        }
      } catch (error) {
        console.error("Failed to fetch data", error);
      } finally {
        setIsShareholdingLoading(false);
        setIsBoardLoading(false);
      }
    };

    fetchData();
  }, [company]);

  useEffect(() => {
    const fetchShareholdingPattern = async () => {
      if (!company?.id && !company?.co_code) {
        return;
      }
      const id = company.co_code?.toString() ?? company.id?.toString();

      try {
        const res = await ScreenerService.getShareholdingPatternData(
          id,
          fromYear,
          toYear
        );
        if (res.success && res.data) {
          // Sort descending by YRC (latest first)
          const sorted = res.data.sort((a, b) => b.YRC - a.YRC);
          setShareholdingPattern(sorted);
          if (sorted.length > 0) {
            // Default to the first (latest) quarter
            setSelectedYRC(sorted[0].YRC);
          } else {
            setSelectedYRC(null);
          }
        }
      } catch (error) {
        console.error("Failed to fetch shareholding pattern", error);
      }
    };

    fetchShareholdingPattern();
  }, [company, fromYear, toYear]);

  const yearOptions = Array.from({ length: 11 }, (_, i) => 2016 + i);

  const formatYRC = (yrc: number) => {
    const yrcStr = yrc.toString();
    if (yrcStr.length !== 6) {
      return yrcStr;
    }
    const year = yrcStr.substring(0, 4);
    const month = yrcStr.substring(4, 6);
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const activeQuarterData = shareholdingPattern.find(
    item => item.YRC === selectedYRC
  ) ??
    shareholdingPattern[0] ?? {
      Promoters: 0,
      ForeignInstitution: 0,
      OtherDomesticInstitution: 0,
      Retail: 0,
      Others: 0,
      MutualFund: 0,
    };

  // Combine DII (MutualFund + OtherDomesticInstitution) for the donut/grid to match UI expectation if needed.
  // The UI snippet showed Promoters, FIIs, DIIs, Public, Others.
  // Our interface has: Promoters, ForeignInstitution, MutualFund, OtherDomesticInstitution, Retail, Others.
  // Mapping:
  // FII -> ForeignInstitution
  // DII -> MutualFund + OtherDomesticInstitution (Typically)
  // Public -> Retail
  // Others -> Others

  const currentShareholding = {
    promoters: activeQuarterData.Promoters?.toFixed(2) ?? "0.00",
    fii: activeQuarterData.ForeignInstitution?.toFixed(2) ?? "0.00",
    dii: (
      (activeQuarterData.MutualFund || 0) +
      (activeQuarterData.OtherDomesticInstitution || 0)
    ).toFixed(2),
    public: activeQuarterData.Retail?.toFixed(2) ?? "0.00",
    others: activeQuarterData.Others?.toFixed(2) ?? "0.00",
  };

  const COMPANY_METADATA = {
    regadd1: company?.regadd1 ?? "N/A",
    regadd2: company?.regadd2,
    regdist: company?.regdist,
    regstate: company?.regstate,
    regpin: company?.regpin,
    tel1: company?.tel1 ?? "N/A", // Not in previous props, using placeholder
    fax1: company?.fax1 ?? "N/A",
    email: company?.email ?? "N/A",
    reg_name: company?.reg_name ?? "N/A",
    reg_tel: company?.reg_tel ?? "N/A",
    reg_fax: company?.reg_fax ?? "N/A",
    reg_email: company?.reg_email ?? "N/A",
    internet: company?.internet ?? "N/A",
    chairman: company?.chairman ?? "N/A",
    co_sec: company?.co_sec ?? "N/A",
    auditor: company?.auditor ?? "N/A", // Not in previous props
  };

  const subsidiaries = subsidiaryJVData.filter(
    item => parseFloat(item.perc_sh) > 50
  );
  const jvs = subsidiaryJVData.filter(
    item => parseFloat(item.perc_sh) >= 20 && parseFloat(item.perc_sh) <= 50
  );
  const collaborations = subsidiaryJVData.filter(
    item => parseFloat(item.perc_sh) < 20
  );

  return (
    <div className="max-w-8xl mx-auto space-y-12 duration-500 animate-in fade-in">
      {/* Shareholding Pattern (Aggregate) */}
      <div className="space-y-6">
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <h3 className="text-lg font-bold text-gray-900">
            Shareholding Pattern
          </h3>

          {/* Period Tabs */}
          <div className="flex flex-col gap-3 md:items-end">
            {/* From and To Year Dropdowns */}
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-600">From:</label>
              <Select value={fromYear} onValueChange={setFromYear}>
                <SelectTrigger className="h-8 w-[80px] bg-white text-[12px]">
                  <SelectValue placeholder="From" />
                </SelectTrigger>
                <SelectContent usePortal={false}>
                  {yearOptions.map(year => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <label className="text-xs font-medium text-gray-600">To:</label>
              <Select value={toYear} onValueChange={setToYear}>
                <SelectTrigger className="h-8 w-[80px] bg-white text-[12px]">
                  <SelectValue placeholder="To" />
                </SelectTrigger>
                <SelectContent usePortal={false}>
                  {yearOptions.map(year => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Period Tabs */}
            <div className="flex w-full max-w-full flex-wrap justify-end gap-1 rounded-lg bg-gray-100 p-1">
              {shareholdingPattern.map(item => (
                <button
                  key={item.YRC}
                  onClick={() => setSelectedYRC(item.YRC)}
                  className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-bold transition-all ${
                    selectedYRC === item.YRC
                      ? "bg-white text-[#00D09C] shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {formatYRC(item.YRC)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm md:p-8 lg:grid-cols-2">
          {/* Interactive Donut Chart */}
          <div className="relative flex min-h-[250px] flex-col items-center justify-center">
            <div className="relative h-48 w-48 md:h-64 md:w-64">
              <svg
                viewBox="0 0 36 36"
                className="h-full w-full -rotate-90 transform transition-all duration-500 ease-out"
              >
                {/* Background */}
                <path
                  className="text-gray-100"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3.8"
                />

                {/* Promoters */}
                <path
                  className="text-[#00D09C] transition-all duration-1000"
                  strokeDasharray={`${currentShareholding.promoters}, 100`}
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3.8"
                />

                {/* FII */}
                <path
                  className="text-blue-500 transition-all duration-1000"
                  strokeDasharray={`${currentShareholding.fii}, 100`}
                  strokeDashoffset={`-${currentShareholding.promoters}`}
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3.8"
                />

                {/* DII */}
                <path
                  className="text-purple-500 transition-all duration-1000"
                  strokeDasharray={`${currentShareholding.dii}, 100`}
                  strokeDashoffset={`-${(parseFloat(currentShareholding.promoters) + parseFloat(currentShareholding.fii)).toFixed(2)}`}
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3.8"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl font-bold text-gray-900 transition-all duration-500 md:text-3xl">
                  {currentShareholding.promoters}%
                </span>
                <span className="text-[10px] font-medium uppercase text-gray-400 md:text-xs">
                  Promoters
                </span>
              </div>
            </div>
          </div>

          {/* Details & Comparison */}
          <div className="space-y-8">
            {/* Grid of details */}
            <div className="grid grid-cols-2 items-center gap-4">
              {[
                {
                  label: "Promoters",
                  value: `${currentShareholding.promoters}%`,
                  color: "bg-[#00D09C]",
                },
                {
                  label: "FIIs",
                  value: `${currentShareholding.fii}%`,
                  color: "bg-blue-500",
                },
                {
                  label: "DIIs",
                  value: `${currentShareholding.dii}%`,
                  color: "bg-purple-500",
                },
                {
                  label: "Public",
                  value: `${currentShareholding.public}%`,
                  color: "bg-gray-300",
                },
                {
                  label: "Others",
                  value: `${currentShareholding.others}%`,
                  color: "bg-orange-400",
                },
              ].map(item => (
                <div
                  key={item.label}
                  className="flex items-center gap-3 rounded-lg p-2 transition-colors hover:bg-gray-50"
                >
                  <div
                    className={`h-3 w-3 rounded-full ${item.color} shrink-0`}
                  />
                  <div>
                    <p className="text-xs font-medium text-gray-500">
                      {item.label}
                    </p>
                    <p className="text-sm font-bold text-gray-900">
                      {item.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Promoter Trend Minimap (Responsive) */}
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
              <div className="mb-4 flex items-center justify-between">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Promoter Holding Trend
                </h4>
                <div className="text-xs font-bold text-slate-500">
                  {currentShareholding.promoters}%
                </div>
              </div>
              <div className="flex h-[100px] w-full items-end justify-between gap-1 overflow-hidden px-2">
                {shareholdingPattern
                  .slice(0, 8)
                  .reverse()
                  .map(item => (
                    <div
                      key={item.YRC}
                      className={`relative w-full rounded-t-sm transition-all hover:opacity-80 ${
                        item.YRC === selectedYRC
                          ? "bg-[#00D09C]"
                          : "bg-blue-400/80"
                      }`}
                      style={{
                        height: `${item.Promoters}%`,
                        minHeight: "10%",
                      }}
                      title={`${formatYRC(item.YRC)}: ${item.Promoters}%`}
                    />
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Shareholding > 1% */}
      <div className="space-y-6">
        <h3 className="text-lg font-bold text-gray-900">
          Shareholding Details (Promoters &gt; 1%)
        </h3>
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white p-6 font-sans shadow-sm">
          <AntDTable
            data={shareholdingData}
            loading={isShareholdingLoading}
            columns={[
              {
                title: "Shareholder Name",
                dataIndex: "Name",
                key: "Name",
                render: (name: string) => (
                  <span className="font-sans font-bold text-gray-700">
                    {name}
                  </span>
                ),
              },
              {
                title: "Category",
                dataIndex: "Type",
                key: "Type",
                render: (type: string) => (
                  <span
                    className={`rounded-full px-3 py-2 font-sans text-[11px] font-bold ${type.includes("Promoter") ? "bg-indigo-50 text-indigo-600" : "bg-emerald-50 text-emerald-600"}`}
                  >
                    {type ?? "N/A"}
                  </span>
                ),
              },
              {
                title: "No. of Shares",
                dataIndex: "NOOFshares",
                key: "NOOFshares",
                className: "text-left font-sans",
                render: (shares: number) => shares.toLocaleString("en-IN"),
              },
              {
                title: "Holding (%)",
                dataIndex: "perstake",
                key: "perstake",
                className: "text-left font-sans",
                render: (stake: number) => `${stake}%`,
              },
            ]}
            size="large"
            pagination={{ pageSize: 10 }}
          />
        </div>
      </div>

      {/* Board of Directors Section */}
      <div className="space-y-6">
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <h3 className="text-lg font-bold text-gray-900">
            Board of Directors
          </h3>

          {/* Year Selector */}
          <div className="no-scrollbar flex max-w-full self-start overflow-x-auto rounded-lg bg-gray-100 p-1 md:self-auto">
            {availableYears.map(year => (
              <button
                key={year}
                onClick={() => setSelectedDirectorYear(year)}
                className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-bold transition-all ${
                  selectedDirectorYear === year
                    ? "bg-white text-[#00D09C] shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {year}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white p-6 font-sans shadow-sm">
          <AntDTable
            data={boardData.filter(d => d.year === selectedDirectorYear)}
            loading={isBoardLoading}
            columns={[
              {
                title: "Sl No.",
                key: "index",
                width: 80,
                render: (_: any, __: any, index: number) => (
                  <span className="font-sans font-medium text-gray-500">
                    {index + 1}
                  </span>
                ),
              },
              {
                title: "Director Name",
                dataIndex: "dir_name",
                key: "dir_name",
                render: (name: string) => (
                  <span className="font-sans font-bold text-gray-700">
                    {name}
                  </span>
                ),
              },
              {
                title: "Designation",
                dataIndex: "dir_desg",
                key: "dir_desg",
                render: (desg: string) => (
                  <span className="rounded bg-gray-100 px-2 py-1 font-sans text-xs font-medium text-gray-600">
                    {desg}
                  </span>
                ),
              },
              {
                title: "Remuneration (₹)",
                dataIndex: "dir_rem",
                key: "dir_rem",
                className: "text-right font-sans",
                render: (rem: string) => {
                  if (!rem || rem === "0.00" || rem === "0") {
                    return "—";
                  }
                  return (
                    <span className="font-bold text-gray-900">
                      {Number(rem.replace(/,/g, "")).toLocaleString("en-IN")}
                    </span>
                  );
                },
              },
            ]}
            size="large"
            pagination={{ pageSize: 12 }}
          />
        </div>
      </div>

      <div className="pt-6" />

      {/* Ownership And Company Information */}
      <div className="space-y-12 border-t border-gray-100 pt-12">
        <h3 className="text-xl font-bold text-gray-900">
          Ownership And Company Information
        </h3>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          {/* Registered Office & Registrar */}
          <div className="space-y-6">
            <div className="space-y-6 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
              <div>
                <h4 className="mb-4 text-xs font-black uppercase tracking-widest text-[#00D09C]">
                  Registered Office
                </h4>
                <div className="space-y-2">
                  <p className="text-sm font-bold text-gray-900">
                    {COMPANY_METADATA.regadd1}
                  </p>
                  <p className="text-sm font-medium text-gray-600">
                    {[
                      COMPANY_METADATA.regadd2,
                      COMPANY_METADATA.regdist,
                      COMPANY_METADATA.regstate,
                      COMPANY_METADATA.regpin,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                  <div className="space-y-1 pt-2 text-xs text-gray-500">
                    <p>
                      <span className="font-semibold">Tel:</span>{" "}
                      {COMPANY_METADATA.tel1}
                    </p>
                    <p>
                      <span className="font-semibold">Fax:</span>{" "}
                      {COMPANY_METADATA.fax1}
                    </p>
                    {COMPANY_METADATA.email !== "N/A" && (
                      <p>
                        <span className="font-semibold">Email:</span>{" "}
                        {COMPANY_METADATA.email}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-50 pt-6">
                <h4 className="mb-4 text-xs font-black uppercase tracking-widest text-[#00D09C]">
                  Registrar Details
                </h4>
                <div className="space-y-2">
                  <p className="text-sm font-bold text-gray-900">
                    {COMPANY_METADATA.reg_name}
                  </p>
                  <p className="text-sm font-medium text-gray-600">
                    {[
                      COMPANY_METADATA.regadd1,
                      COMPANY_METADATA.regadd2,
                      COMPANY_METADATA.regdist,
                      COMPANY_METADATA.regstate,
                      COMPANY_METADATA.regpin,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                  <div className="space-y-1 pt-2 text-xs text-gray-500">
                    <p>
                      <span className="font-semibold">Tel:</span>{" "}
                      {COMPANY_METADATA.reg_tel}
                    </p>
                    <p>
                      <span className="font-semibold">Fax:</span>{" "}
                      {COMPANY_METADATA.reg_fax}
                    </p>
                    {COMPANY_METADATA.reg_email !== "N/A" && (
                      <p>
                        <span className="font-semibold">Email:</span>{" "}
                        <a
                          href={`mailto:${COMPANY_METADATA.reg_email}`}
                          className="text-blue-500 hover:underline"
                        >
                          {COMPANY_METADATA.reg_email}
                        </a>
                      </p>
                    )}
                    {COMPANY_METADATA.internet !== "N/A" && (
                      <p>
                        <span className="font-semibold">Web:</span>{" "}
                        <a
                          href={`https://${COMPANY_METADATA.internet}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-500 hover:underline"
                        >
                          {COMPANY_METADATA.internet}
                        </a>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Management & Bankers */}
          <div className="space-y-6">
            <div className="space-y-6 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
              <div>
                <h4 className="mb-4 text-sm font-black uppercase tracking-widest text-[#00D09C]">
                  Management
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[11px] font-bold uppercase text-gray-400">
                      Chairman
                    </p>
                    <p className="text-sm font-bold text-gray-900">
                      {COMPANY_METADATA.chairman}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] font-bold uppercase text-gray-400">
                      Company Secretary
                    </p>
                    <p className="text-sm font-bold text-gray-900">
                      {COMPANY_METADATA.co_sec}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-[11px] font-bold uppercase text-gray-400">
                      Auditor
                    </p>
                    <p className="text-sm font-bold text-gray-900">
                      {COMPANY_METADATA.auditor}
                    </p>
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-50 pt-6">
                <h4 className="mb-4 text-sm font-black uppercase tracking-widest text-[#00D09C]">
                  Bankers
                </h4>
                <div className="flex flex-wrap gap-2">
                  {(showAllBankers
                    ? bankersData
                    : bankersData.slice(0, 10)
                  ).map((bank: BankersRow) => (
                    <span
                      key={bank.id}
                      className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-1.5 text-[11px] font-bold text-gray-600"
                    >
                      {bank.bnk_name}
                    </span>
                  ))}
                  {!showAllBankers && bankersData.length > 10 && (
                    <span
                      onClick={() => setShowAllBankers(true)}
                      className="cursor-pointer select-none rounded-lg border border-gray-100 bg-gray-50 px-3 py-1.5 text-[11px] font-bold text-gray-400 hover:bg-gray-100"
                    >
                      +{bankersData.length - 10} More
                    </span>
                  )}
                  {showAllBankers && bankersData.length > 10 && (
                    <span
                      onClick={() => setShowAllBankers(false)}
                      className="cursor-pointer select-none rounded-lg border border-gray-100 bg-gray-50 px-3 py-1.5 text-[11px] font-bold text-gray-400 hover:bg-gray-100"
                    >
                      Show Less
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Subsidiaries, JVs, collaborations */}
        <div className="space-y-6">
          <h4 className="text-lg font-bold text-gray-900">
            Subsidiary, JV, collaboration
          </h4>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
              <h5 className="mb-4 text-[11px] font-black uppercase tracking-widest text-blue-500">
                Subsidiaries
              </h5>
              <div className="flex flex-col gap-2">
                {subsidiaries.length > 0 ? (
                  subsidiaries.map(sub => (
                    <span
                      key={sub.id}
                      className="flex items-center gap-2 text-sm font-medium text-gray-700"
                    >
                      <div className="h-1.5 w-1.5 rounded-full bg-blue-500" />{" "}
                      {sub.coll_name}
                    </span>
                  ))
                ) : (
                  <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <div className="h-1.5 w-1.5 rounded-full bg-blue-500" />{" "}
                    None
                  </span>
                )}
              </div>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
              <h5 className="mb-4 text-[11px] font-black uppercase tracking-widest text-purple-500">
                Joint Ventures (JVs)
              </h5>
              <div className="flex flex-col gap-2">
                {jvs.length > 0 ? (
                  jvs.map(jv => (
                    <span
                      key={jv.id}
                      className="flex items-center gap-2 text-sm font-medium text-gray-700"
                    >
                      <div className="h-1.5 w-1.5 rounded-full bg-purple-500" />{" "}
                      {jv.coll_name}
                    </span>
                  ))
                ) : (
                  <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <div className="h-1.5 w-1.5 rounded-full bg-purple-500" />{" "}
                    None
                  </span>
                )}
              </div>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
              <h5 className="mb-4 text-[11px] font-black uppercase tracking-widest text-[#00D09C]">
                Collaborations
              </h5>
              <div className="flex flex-col gap-2">
                {collaborations.length > 0 ? (
                  collaborations.map(collab => (
                    <span
                      key={collab.id}
                      className="flex items-center gap-2 text-sm font-medium text-gray-700"
                    >
                      <div className="h-1.5 w-1.5 rounded-full bg-[#00D09C]" />{" "}
                      {collab.coll_name}
                    </span>
                  ))
                ) : (
                  <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <div className="h-1.5 w-1.5 rounded-full bg-[#00D09C]" />{" "}
                    None
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnershipTab;
